/**
 * True when an error is the auth lock failing to be acquired, in any of the
 * shapes it reaches application code. The browser client uses auth-js's
 * in-tab `processLock` (see client.ts); the Web Lock shapes are recognised
 * too, in case the lock is ever switched back:
 *  - the raw DOMException auth-js rejects with (`AbortError`, no reason)
 *  - auth-js's `ProcessLockAcquireTimeoutError` / `NavigatorLockAcquireTimeoutError` (`isAcquireTimeout`)
 *  - processLock's timeout wrapped by PostgREST ("Error: Acquiring process lock … timed out")
 *  - the PostgREST-wrapped form supabase-js hands to `.from()/.rpc()` callers
 *    (`message: "AbortError: signal is aborted…"`, `hint: "Request was aborted"`)
 *
 * It says NOTHING about the session or the data: the request never left the
 * device. Callers must never clear the session or render an empty state on it.
 */
export function isAuthLockFailure(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const e = error as { name?: unknown; message?: unknown; hint?: unknown; isAcquireTimeout?: unknown }

  if (e.isAcquireTimeout === true) return true
  if (e.name === 'NavigatorLockAcquireTimeoutError' || e.name === 'LockAcquireTimeoutError') return true

  const message = typeof e.message === 'string' ? e.message.toLowerCase() : ''
  const hint = typeof e.hint === 'string' ? e.hint.toLowerCase() : ''

  if (e.name === 'AbortError') {
    return message.includes('aborted') || message.includes('lock')
  }
  // supabase-js stringifies the rejection: "AbortError: signal is aborted without reason"
  if (message.startsWith('aborterror') && message.includes('aborted')) return true
  if (message.includes('lock broken by another request')) return true
  // processLock's timeout is a plain Error: PostgREST wraps it as
  // "Error: Acquiring process lock with name … timed out".
  if (message.includes('acquiring') && message.includes('lock') && message.includes('timed out')) return true
  if (hint === 'request was aborted' && message.includes('abort')) return true

  return false
}
